import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon
} from '../../../../components/ui/Icons';
import { Badge } from '../../../../components/ui';
import dayjs from 'dayjs';

const ApprovalStatusSection = ({ selectedRecord }) => {
  const { theme } = useTheme();

  const getStatusIcon = (status) => {
    const statusStr = typeof status === 'string' ? status.toLowerCase() : (status?.name || status || '').toLowerCase();
    switch (statusStr) {
      case 'pending':
        return <ClockIcon className={cn("h-5 w-5", theme.text.warning)} />;
      case 'approved':
        return <CheckCircleIcon className={cn("h-5 w-5", theme.text.success)} />;
      case 'rejected':
        return <XCircleIcon className={cn("h-5 w-5", theme.text.danger)} />;
      case 'cancelled':
        return <ExclamationTriangleIcon className={cn("h-5 w-5", theme.text.muted)} />;
      default:
        return <ClockIcon className={cn("h-5 w-5", theme.text.muted)} />;
    }
  };

  const isOverdue = selectedRecord?.dueDate && new Date(selectedRecord.dueDate) < new Date();
  const statusValue = selectedRecord?.status?.name || selectedRecord?.status || 'unknown';

  return (
    <>
      {/* Status and Priority Header */}
      <div className={cn("p-3 rounded-lg border", theme.colors.surface, theme.border.light)}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={cn("text-lg font-bold", theme.text.primary)}>
            {selectedRecord.title}
          </h2>
          <div className="flex items-center space-x-2">
            {getStatusIcon(statusValue)}
            <Badge 
              variant={statusValue?.toLowerCase() === 'pending' ? 'warning' : 
                     statusValue?.toLowerCase() === 'approved' ? 'success' : 
                     statusValue?.toLowerCase() === 'rejected' ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {statusValue?.charAt(0).toUpperCase() + statusValue?.slice(1)}
            </Badge>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Badge 
            variant={selectedRecord.priority === 'high' ? 'destructive' : 
                   selectedRecord.priority === 'medium' ? 'warning' : 'secondary'}
            className="text-xs"
          >
            {(selectedRecord.priority?.name || selectedRecord.priority)?.charAt(0).toUpperCase() + (selectedRecord.priority?.name || selectedRecord.priority)?.slice(1)} Priority
          </Badge>
          {isOverdue && (
            <Badge variant="destructive" className="text-xs">
              <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
              Overdue
            </Badge>
          )}
        </div>
        
        <p className={cn("text-xs font-mono", theme.text.secondary)}>
          {selectedRecord.code}
        </p>
      </div>

      {/* Key Information */}
      <div className="space-y-3 mt-4">
        {/* Workflow Type */}
        <div className={cn("flex items-center justify-between p-3 rounded-lg", theme.colors.background, theme.border.subtle)}>
          <div className="flex items-center space-x-3">
            <CheckCircleIcon className={cn("w-4 h-4", theme.text.primary)} />
            <span className={cn("text-sm font-medium", theme.text.secondary)}>Workflow</span>
          </div>
          <Badge variant="outline">
            {selectedRecord.workflowType === 'review' ? 'Review Only' :
             selectedRecord.workflowType === 'approval' ? 'Approval Only' :
             selectedRecord.workflowType === 'review_approval' ? 'Review & Approval' :
             'Standard'}
          </Badge>
        </div>

        {/* Due Date */}
        {selectedRecord.dueDate && (
          <div className={cn("flex items-center justify-between p-3 rounded-lg", theme.colors.background, theme.border.subtle)}>
            <div className="flex items-center space-x-3">
              <CalendarIcon className={cn("w-4 h-4", isOverdue ? theme.text.danger : theme.text.warning)} />
              <span className={cn("text-sm font-medium", theme.text.secondary)}>Due Date</span>
            </div>
            <div className="text-right">
              <span className={cn("text-sm font-semibold", isOverdue ? theme.text.danger : theme.text.primary)}>
                {dayjs(selectedRecord.dueDate).format('MMM DD, YYYY')}
              </span>
              {isOverdue && (
                <p className={cn("text-xs", theme.text.danger)}>⚠️ Overdue</p>
              )}
            </div>
          </div>
        )}

        {/* Requested By */}
        {selectedRecord.requestedBy && (
          <div className={cn("flex items-center justify-between p-3 rounded-lg", theme.colors.background, theme.border.subtle)}>
            <div className="flex items-center space-x-3">
              <UserIcon className={cn("w-4 h-4", theme.text.success)} />
              <span className={cn("text-sm font-medium", theme.text.secondary)}>Requested By</span>
            </div>
            <span className={cn("text-sm font-semibold", theme.text.primary)}>
              {selectedRecord.requestedBy.name || selectedRecord.requestedBy.userDisplayName || 'Unknown User'}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default ApprovalStatusSection;
